import React from "react"
import { ThemeProvider } from "@emotion/react"
import { useStaticQuery, graphql } from "gatsby"
import { Link } from "gatsby"
import styled from "@emotion/styled"
import { Box, Container, useThemeUI } from "theme-ui"

const MainNav = styled.ul`
    list-style: none;
    padding: 0 10px;
    margin: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
`
const LinkList = styled.li`
    height: 100%;
    list-style: none;
    padding: 0;
    margin: 0;
    &:hover .megamenu {
        display: block;
    }

    & a {
        height: 100%;
        color: ${(props) => props.theme.colors.text1};
    }

    &:hover > a,
    &:active > a,
    & > a.active {
        color: ${(props) => props.theme.colors.secondary};
    }

    &:hover > a:after,
    &:active > a:after,
    & > a.active:after {
        border-color: ${(props) => props.theme.colors.secondary};
    }
`
const MenuLink = styled(Link)`
    &,
    &:hover,
    &:visited {
        display: inline-block;
        text-decoration: none;
        padding: 3rem 1rem;
        text-transform: capitalize;
        font-weight: normal;
        color: ${(props) => props.theme.colors.dark_grey_1};
        padding-bottom: 1rem;

        @media screen and (max-width: ${(props) =>
                props.theme.breakpoints[8]}) {
            padding: 3rem 0.7rem;
            text-align: center;
        }

        @media screen and (max-width: ${(props) =>
                props.theme.breakpoints[7]}) {
            padding: 3rem 0.5rem;
            text-align: center;
        }

        @media screen and (max-width: ${(props) =>
                props.theme.breakpoints[6]}) {
            text-align: center;
        }
    }

    &:after {
        content: " ";
        width: 0px;
        height: 2px;
        display: block;
        margin: 0 auto;
        border-bottom: 2px solid white;
        transition: all 0.3s ease-in;
    }
`
const MegaMenu = styled.div`
    display: none;
    width: 100%;
    min-height: 150px;
    position: absolute;
    padding: 1em 0;
    left: 0;
    z-index: 99;
    background-color: #fafafa;
    transition: all 0.5s ease-in;
    box-shadow: 0 4px 8px rgb(0, 0, 0, 0.16);
`
const MenuTitle = styled.div`
    max-width: 350px;
    padding-right: 1em;
    font-size: 3em;
    font-weight: bold;
    line-height: 1em;
    text-transform: uppercase;
    opacity: 0.08;
    flex: 1;
`
const SectionWrap = styled.div`
    display: flex;
    justify-content: space-between;
    & > div:last-child() {
        flex: 1;
    }
`
const Section = styled.div`
    font-size: ${(props) => props.theme.fontSizes[2]}px;
    text-transform: capitalize;
    white-space: nowrap;
    a {
        &,
        &:hover,
        &:visited {
            display: inline-block;
            text-decoration: none;
            padding: 10px 20px;
            text-transform: capitalise;
            font-weight: normal;
            color: ${(props) => props.theme.colors.dark_grey_1};
        }

        &:hover {
            color: ${(props) => props.theme.colors.blue1};
        }
        &.parent {
            font-weight: bold;
        }
    }

    ul,
    li {
        list-style: none;
        padding: 0;
        margin: 0;
    }
    &.leftBorder {
        border-left: 1px solid #00000029;
    }
`

const Submenu = styled.ul`
    margin-left: 1rem;
    margin-right: 1rem !important;

    & ul {
        padding-right: 1rem;
        & li {
            position: relative;
        }
    }

    & .border-bottom-link {
        // border-bottom: 1px solid #00000029;
        position: absolute;
        left: 1rem;
        right: 0;
    }
`

const SubmenuLink = styled(Link)`
    &,
    &:hover,
    &:visited {
        display: inline-block;
        text-decoration: none;
        padding: 5px 20px;
        text-transform: capitalise;
        font-weight: normal;
        line-height: 1.1;
    }

    &:hover {
        color: ${(props) => props.theme.colors.secondary};
    }

    &:before {
        content: " ";
        font-size: 1.2em;
        // padding-right: 10px;
        display: inline;
        transition: all 0.5s ease-in;
    }
`

const Nav = () => {
    const context = useThemeUI()
    const { theme } = context
    const menu = useStaticQuery(menuQuery)
    const submenu = menu.strapiMenu.sub_menus.sort(function (a, b) {
        return a.section - b.section
    })

    const gerateSectionMenu = (linkItem, sectionNumber) => {
        if (
            linkItem.submenu.filter((child) => child.section === sectionNumber)
                .length > 0
        ) {
            const sectionMenu = linkItem.submenu
                .filter((child) => child.section === sectionNumber)
                .map((subLink, index) => {
                    let subMenuSection1 = null
                    if (subLink.childmenu && subLink.childmenu.length > 0) {
                        subMenuSection1 = (
                            <Submenu>
                                <ul>
                                    {subLink.childmenu.map((subsubLink) => (
                                        <li key={subsubLink.id}>
                                            <SubmenuLink
                                                to={`${subLink.link}/${subsubLink.link}`}
                                            >
                                                {subsubLink.name}
                                            </SubmenuLink>
                                            <div className="border-bottom-link" />
                                        </li>
                                    ))}
                                </ul>
                            </Submenu>
                        )
                    }
                    return <div key={subLink.id}>{subMenuSection1}</div>
                })
            return <Section className="leftBorder">{sectionMenu}</Section>
        }
        return null
    }
    // console.log(submenu)

    const menuLinks = submenu.map((linkItem, index) => {
        let menuList = null
        if (linkItem.submenu && linkItem.submenu.length > 0) {
            const menuSection1 = gerateSectionMenu(linkItem, "1")
            const menuSection2 = gerateSectionMenu(linkItem, "2")

            menuList = (
                <MegaMenu
                    className="megamenu"
                    data-id={linkItem.id}
                    aria-label="submenu"
                    aria-hidden="true"
                >
                    <Container
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-start",
                        }}
                    >
                        <MenuTitle>
                            {linkItem.displayname || linkItem.name}
                        </MenuTitle>
                        <SectionWrap>
                            {menuSection1}
                            {menuSection2}
                        </SectionWrap>
                    </Container>
                </MegaMenu>
            )
        }

        return (
            // Top Level Links
            <LinkList key={index}>
                <MenuLink to={linkItem.url} activeClassName="active">
                    {linkItem.displayname || linkItem.name}
                </MenuLink>
                {menuList}
            </LinkList>
        )
    })

    return (
        <ThemeProvider theme={theme}>
            <Box sx={{ height: "100%" }}>
                <MainNav>{menuLinks}</MainNav>
            </Box>
        </ThemeProvider>
    )
}

export default Nav

const menuQuery = graphql`
    {
        strapiMenu(
            menu_type: { eq: "header" }
            company: { shortname: { eq: "WNS" } }
            sub_menus: {}
        ) {
            sub_menus {
                menu
                name
                displayname
                section
                submenu {
                    id
                    link
                    name
                    section
                    childmenu {
                        id
                        link
                        name
                    }
                }
                url
            }
            name
        }
    }
`
